import { ExternalLink, Users, MessageCircle, Star, Heart } from 'lucide-react';
import { cn } from '../lib/utils';
import { SUBREDDIT as subredditData } from '../lib/subreddit';
import { useSubredditStats } from '../hooks/useSubredditStats';

interface SubredditWidgetProps {
  className?: string;
  'data-testid'?: string;
}

export function SubredditWidget({
  className,
  'data-testid': testId
}: SubredditWidgetProps) {
  const { members } = useSubredditStats();

  const handleVisitSubreddit = () => {
    window.open(subredditData.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        "relative rounded-lg overflow-hidden transition-all duration-300 cursor-pointer group",
        "bg-gradient-to-br from-orange-900/20 to-red-900/10 border border-orange-500/30 shadow-lg",
        "hover:shadow-xl hover:shadow-orange-500/10 hover:border-orange-400/50",
        "backdrop-blur-sm",
        className
      )}>
      {/* Sci-fi corner accents - orange/red theme */}
      <div className="absolute top-0 left-0 w-[40px] h-[1px] bg-gradient-to-r from-orange-400/70 to-transparent" />
      <div className="absolute top-0 left-0 w-[1px] h-[40px] bg-gradient-to-b from-orange-400/70 to-transparent" />
      <div className="absolute top-0 right-0 w-[40px] h-[1px] bg-gradient-to-l from-orange-400/70 to-transparent" />
      <div className="absolute top-0 right-0 w-[1px] h-[40px] bg-gradient-to-b from-orange-400/70 to-transparent" />

      {/* Header */}
      <div className="p-4 pb-3 border-b border-orange-500/20">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-orange-400 flex items-center gap-2">
            <Heart className="h-5 w-5 text-orange-400" />
            Community Partner
          </h3>
          <div className="bg-orange-500/10 text-orange-400 border border-orange-500/30 flex items-center gap-1 px-2 py-1 text-xs rounded-full">
            <Star className="h-3 w-3" />
            Collab
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Reddit-style header */}
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30 p-6">
          {/* Reddit-style background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-2 left-2 w-3 h-3 bg-orange-400 rounded-full"></div>
            <div className="absolute top-6 right-4 w-2 h-2 bg-red-400 rounded-full"></div>
            <div className="absolute bottom-3 left-8 w-4 h-4 bg-orange-300 rounded-full"></div>
            <div className="absolute bottom-6 right-2 w-2 h-2 bg-red-300 rounded-full"></div>
          </div>

          {/* Subreddit info */}
          <div className="relative text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              <MessageCircle className="h-8 w-8 text-orange-400" />
              <h4 className="text-2xl font-bold text-white">{subredditData.fullName}</h4>
            </div>
            <p className="text-gray-300 text-sm max-w-sm mx-auto leading-relaxed">
              {subredditData.description}
            </p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-orange-300">
                <Users className="h-4 w-4" />
                <span>{members} members</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={handleVisitSubreddit}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium h-10 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 border-0 cursor-pointer"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="text-sm">Visit r/FromAfar</span>
          </button>
        </div>
      </div>
    </div>
  );
}
